/**
 * Theme Switcher Component
 * Allows users to switch between Light, Dark, and System mode
 */

import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';

export function ThemeSwitcher() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  const themes = [
    { code: 'light', name: t('theme.light'), icon: Sun },
    { code: 'dark', name: t('theme.dark'), icon: Moon },
    { code: 'system', name: t('theme.system'), icon: Monitor },
  ];

  const currentTheme = themes.find((t) => t.code === theme) || themes[0];
  const Icon = currentTheme.icon;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        title={t('theme.title')}
      >
        <Icon size={18} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 animate-fade-in overflow-hidden">
          {themes.map((t) => {
            const ThemeIcon = t.icon;
            const isActive = t.code === theme;
            return (
              <button
                key={t.code}
                onClick={() => handleThemeChange(t.code as 'light' | 'dark' | 'system')}
                className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center gap-3 whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-2 flex-1">
                  <ThemeIcon size={16} />
                  <span>{t.name}</span>
                </div>
                {isActive && (
                  <span className="text-primary-600 dark:text-primary-400">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
